(() => {
    window.Dashboard = Dashboard || {};

    window.Dashboard.Models = window.Dashboard.Models || {};

    /**
     * The Campaign model for Yumbi Campaigns
     * @constructor
     */
    class CampaignTemplate {
        /**
         * The Id of the campaign template
         * @type {number}
         */
        Id = 0;

        /**
         * The template guid
         * @type {string|undefined}
         */
        TemplateGuid = "";

        /**
         * The group id
         * @type {number}
         */
        GroupId = 0;

        /**
         * The group name
         * @type {string}
         */
        Group = "";

        /**
         * The name of the campaign template
         * @type {string}
         */
        Name = "";

        /**
         * The description of the campaign template
         * @type {string}
         */
        Description = "";

        /**
         * The customer source description
         * @type {string}
         */
        CustomerSourceDescription = "";

        /**
         * The customer source url
         * @type {string}
         */
        CustomerSourceUrl = undefined;

        /**
         * The campaign template state
         * @type {number}
         */
        StateId = 1;

        /**
         * The campaign template level
         * @type {string}
         */
        CampaignLevelId = Dashboard.Models.Lookups.CampaignLevels.Brand;

        /**
         * The brand id
         * @type {number}
         */
        BrandId = undefined;

        /**
         * The brand name
         * @type {string}
         */
        Brand = undefined;

        /**
         * The publish from date for the campaign template
         * @type {Date}
         */
        PublishFrom = new Date();

        /**
         * The publish to date for the campaign template
         * @type {Date}
         */
        PublishTo = new Date();

        /**
         * The start date for the campaign template
         * @type {Date}
         */
        StartDate = new Date();

        /**
         * The end date for the campaign template
         * @type {Date}
         */
        EndDate = new Date();

        /**
         * The terms for the campaign template
         * @type {string}
         */
        Terms = "";

        /**
         * The terms url for the campaign template
         * @type {string}
         */
        TermsUrl = "";

        /**
         * The lead time work days
         * @type {number}
         */
        LeadTimeWorkdays = 0;

        /**
         * The cool off period
         * @type {number}
         */
        CoolOffPeriod = 0;

        /**
         * The reporting period length
         * @type {number}
         */
        ReportingPeriodLength = 30;

        /**
         * The start date rule
         * @type {number}
         */
        StartDateRule = 1;

        /**
         * The end date rule
         * @type {string}
         */
        EndDateRule = 1;

        /**
         * The customer workflow type
         * @type {string}
         */
        CustomerWorkflowType = "";

        /**
         * A collection of associated store associations
         * @type {StoreAssociation[]}
         */
        StoreAssociations = [];

        /**
         * The campaign voucher templates
         * @type {CampaignTemplateVoucher[]}
         */
        Vouchers = [];

        /**
         * The campaign voucher communications
         * @type {Dashboard.Models.CampaignTemplateCommunication[]}
         */
        Communications = [];

        /**
         * The date format to be used for the date variables
         * @type {string}
         */
        DateFormat = "";

        /**
         * The time format to be used for the date variables
         * @type {string}
         */
        TimeFormat = "";

        MenuScripts = [];

        /**
         * The min duration of the campaign in days
         * @type {int}
         */
        MinDuration = 1;

        /**
         * The max duration of the campaign in days
         * @type {int}
         */
        MaxDuration = 1;

        IsAvailableToAllStores = false;

        AllowCustomDates = true;

        CampaignObjectives = [];

        CampaignTemplateTargetSources = [];

        LandingPageURL = "";

        AvailableStartDates = [];

        /**
         * @param {Partial<CampaignTemplate>} [props]
         */
        constructor(props) {
            if (typeof props !== "object") {
                return;
            }
            for (const k of Object.keys(props)) {
                this[k] = props[k];
            }
        }

        /**
         * Initialises the campaign with the campaign returned from the API
         */
        InitialiseCampaignTemplate(campaignTemplate) {
            _.extend(this, campaignTemplate);
            this.Communications = [];
            campaignTemplate.Communications.forEach(comm => {
                this.Communications.push(new Dashboard.Models.CampaignTemplateCommunication()
                    .initialiseCampaignTemplateCommunication(comm));
            });
            this.StoreAssociations = this.StoreAssociations || [];
            const haveAllStoresMarker = !!this.StoreAssociations.find(o => o.Id === 0);
            const haveNoStoreAssociations = this.StoreAssociations.length === 0;
            const onlyHaveAllStoresMarker = this.StoreAssociations.length === 1 &&
                haveAllStoresMarker;
            this.IsAvailableToAllStores = haveNoStoreAssociations || onlyHaveAllStoresMarker;
            if (haveAllStoresMarker && !onlyHaveAllStoresMarker) {
                this.StoreAssociations = this.StoreAssociations.filter(o => o.Id !== 0);
            }
            return this;
        }

        /**
         * Validates the campaign template
         * @param { ErrorService } errorService
         * @returns {boolean}
         */
        Validate(errorService) {
            let validationPassed = true;

            if (!this.Name) {
                errorService.showError('Name Required', null);
                validationPassed = false;
            }

            if (!this.Description) {
                errorService.showError('Description Required', null);
                validationPassed = false;
            }

            if (!this.Terms) {
                errorService.showError('Terms Required', null);
                validationPassed = false;
            }

            if ((this.CampaignTemplateTargetSources || []).length === 0) {
                errorService.showError('At least one target source required', null);
                validationPassed = false;
            }

            if (this.MinDuration < 0) {
                errorService.showError('Minimum duration must be greater than zero', null);
                validationPassed = false;
            }

            if (this.MinDuration > this.MaxDuration) {
                errorService.showError('Minimum duration must not exceed Maximum duration', null);
                validationPassed = false;
            }

            if (this.PublishFrom > this.PublishTo) {
                errorService.showError('Publish from date must not exceed publish to date', null);
                validationPassed = false;
            }

            return validationPassed;
        }

        /**
         * Returns the formatted Terms URL
         * @return {string}
         */
        GetFormattedTermsUrl() {
            let url = this.TermsUrl;
            if (!url) {
                return "";
            }

            const urlLowerCase = url.toLowerCase();

            if (!urlLowerCase.startsWith("http://") && !urlLowerCase.startsWith("https://")) {
                url = "http://" + url;
            }
            return url;
        }

        /**
         * True if campaign is in draft state
         * {boolean}
         */
        IsDraft() {
            return this.StateId === window.Dashboard.Models.Lookups.CampaignTemplateStates.Draft;
        }

        /**
         * True if campaign is in review state
         * {boolean}
         */
        IsInReview() {
            return this.StateId === window.Dashboard.Models.Lookups.CampaignTemplateStates.InReview;
        }

        /**
         * True if campaign is in published state
         * {boolean}
         */
        IsPublished() {
            return this.StateId === window.Dashboard.Models.Lookups.CampaignTemplateStates.Published;
        }

        /**
         *  True if campaign is a new campaign
         * {boolean}
         */
        IsNewCampaignTemplate() {
            return !this.TemplateGuid ||
                this.TemplateGuid === window.Dashboard.Constants.EmptyGuid;
        }

        /**
         * True if campaign has communication
         * {boolean}
         * @return {boolean}
         */
        HasCommunication() {
            if (!this.Communications
                || this.Communications.length === 0) {
                return false;
            }

            const communicationWithMessages = this.Communications.filter(
                comm => isCommunication(comm) && (
                    comm.HasSMS() ||
                    comm.HasInboxMessage() ||
                    comm.HasPushNotification()
                )
            );

            return communicationWithMessages.length > 0;
        }
    };

    /**
     * @param {any} obj
     * @returns {boolean}
     */
    function isCommunication(obj) {
        if (!obj) {
            return false;
        }
        for (const fn of [ "HasSMS", "HasInboxMessage", "HasPushNotification" ]) {
            if (typeof obj[fn] !== "function") {
                return false;
            }
        }
        return true;
    }

    window.Dashboard.Models.CampaignTemplate = CampaignTemplate;
})();
